/**
 * API Router - Dynamic Lambda URL routing
 * 
 * This helper automatically routes API calls to the correct Lambda function
 * based on the action type and microservices configuration.
 * Matches the pattern used in the React Native app.
 */

import { getLambdaUrl, type HandlerType } from '@/config/lambdaConfig';

/**
 * Action to handler type mapping
 * Maps API actions to their corresponding microservice handler types
 */
const ACTION_TO_HANDLER: Record<string, HandlerType> = {
  // Auth actions → auth microservice
  'signIn': 'auth',
  'signUp': 'auth',
  'verify': 'auth',
  'auth_signin': 'auth',
  'auth_signup': 'auth',
  'auth_verify': 'auth',
  
  // Profile actions → profiles microservice
  'save_user_profiles': 'profiles',
  'get_user_profiles': 'profiles',
  'delete_profile_data': 'profiles',
  
  // Story actions → stories microservice
  'get_user_stories': 'stories',
  'save_story': 'stories',
  'clear_user_stories': 'stories',
  'delete_story': 'stories',
  'save_words': 'stories',
  'get_words': 'stories',
  'migrate_guest_data': 'stories',
  
  // Teacher actions → teachers microservice
  'teacher_register': 'teachers',
  'check_teacher_status': 'teachers',
  'get_teacher_profile': 'teachers',
  'update_teacher_profile': 'teachers',
  'update_teacher_password': 'teachers',
  'admin_create_teacher': 'teachers',
  'create_teacher': 'teachers',
  'resend_teacher_verification': 'teachers',
  'reset_teacher_password': 'teachers',
  'get_school_teachers': 'teachers',
  'get_teacher_students': 'teachers',
  'delete_teacher': 'teachers',
  'get_pending_teachers': 'teachers',
  'approve_teacher': 'teachers',
  'reject_teacher': 'teachers',
  'promote_to_admin': 'teachers',
  'make_teacher_admin': 'teachers',
  'remove_teacher_admin': 'teachers',
  'get_school_info': 'teachers',
  'check_teacher_license': 'teachers',
  'update_teacher_class_limit': 'teachers',
  'deactivate_teacher': 'teachers',
  
  // Student actions → students microservice
  'student_register': 'students',
  'student_login': 'students',
  'student_join_class': 'students',
  'get_student_classes': 'students',
  'get_student_wordlists': 'students',
  'log_student_activity': 'students',
  'get_student_activity': 'students',
  'get_class_students': 'students',
  'get_school_students': 'students',
  'get_teacher_students_list': 'students',
  'get_student': 'students',
  'get_student_by_id': 'students',
  'create_student': 'students',
  'add_student_to_class': 'students',
  'remove_student_from_class': 'students',
  'update_student': 'students',
  'delete_student': 'students',
  'bulk_delete_students': 'students',
  'reset_student_password': 'students',
  'disconnect_parent': 'students',
  
  // Class actions → classes microservice
  'create_class': 'classes',
  'get_teacher_classes': 'classes',
  'get_school_classes': 'classes',
  'get_class': 'classes',
  'get_class_details': 'classes',
  'update_class': 'classes',
  'delete_class': 'classes',
  
  // Word list actions → wordlists microservice
  'create_wordlist': 'wordlists',
  'get_teacher_wordlists': 'wordlists',
  'get_class_wordlists': 'wordlists',
  'get_wordlist': 'wordlists',
  'get_active_wordlists': 'wordlists',
  'update_wordlist': 'wordlists',
  'set_current_wordlist': 'wordlists',
  'delete_wordlist': 'wordlists',
  'get_all_school_wordlists': 'wordlists',
  
  // Parent actions → parents microservice
  'parent_consent': 'parents',
  'get_parent_students': 'parents',
  'get_student_parents': 'parents',
  'create_parent_account': 'parents',
  'link_parent_student': 'parents',
  'get_parent_activity': 'parents',
  'check_school_user_status': 'parents',
  'get_school_user_profile': 'parents',
  'request_student_deletion': 'parents',
  'get_deletion_requests': 'parents',
  'approve_deletion_request': 'parents',
  'reject_deletion_request': 'parents',
  'disconnect_parent_from_student': 'parents',
  
  // Licensing actions → licensing microservice
  'create_school_license': 'licensing',
  'assign_teacher_license': 'licensing',
  'check_teacher_license_status': 'licensing',
  'get_class_activity': 'licensing',
  'get_school_usage': 'licensing',
};

/**
 * Get the appropriate Lambda URL for an action
 */
export function getLambdaUrlForAction(action: string): string {
  const handlerType = ACTION_TO_HANDLER[action];
  
  if (handlerType) {
    const url = getLambdaUrl(handlerType);
    if (import.meta.env.DEV) {
      console.log(`[URL Routing] ${action} → ${handlerType} → ${url}`);
    }
    return url;
  }
  
  // Fallback to education Lambda for unknown actions (most web-admin actions are education-related)
  const fallbackUrl = getLambdaUrl('teachers'); // Uses education monolith
  if (import.meta.env.DEV) {
    console.log(`[URL Routing] ${action} → UNKNOWN → ${fallbackUrl}`);
  }
  return fallbackUrl;
}
